import React, { useEffect, useState, useMemo } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import '../../App.css';
import '../../scss/App.scss';
import './temple-detail.scss';
import { Box, Container, Grid, Link, Typography } from '@mui/material';
import { getTempleBySlug } from '../../data/templesData.js';

export default function TempleDetail() {
  const { slug } = useParams();

  // If someone requests any of the 5 famous legacy temples on /temple/:slug,
  // redirect immediately to their dedicated static page so they don't load from JSON
  const dedicatedRoute = useMemo(() => {
    if (!slug) return null;
    const s = slug.toLowerCase();
    if (s.includes('vaishno')) return '/vaishno-devi';
    if (s.includes('dwarka')) return '/dwarkadhish-temple';
    if (s.includes('ayodhya') && !s.includes('hanuman-garhi')) return '/ayodhya';
    if (s.includes('kashi') || (s.includes('vishwanath') && !s.includes('kamakhya'))) return '/kashi-vishwanath';
    if (s.includes('rameshwaram') || s.includes('ramanathaswamy')) return '/ramanathaswamy-temple';
    return null;
  }, [slug]);

  const [showAllMonths, setShowAllMonths] = useState(false);

  // Active temple resolution
  const temple = useMemo(() => getTempleBySlug(slug), [slug]);

  // Scroll to top & set document title
  useEffect(() => {
    if (temple?.generalInfo?.templeName) {
      document.title = `${temple.generalInfo.templeName} - Timings, Aartis, Route & Guide | MandirWiki`;
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [temple]);

  // Helper: Seasonal temperatures calculation from 12-month data
  const seasonTemps = useMemo(() => {
    const list = temple.temperature?.monthlyTemperatures || [];
    const getRange = (months) => {
      const match = list.filter((m) => months.includes(m.month));
      if (!match.length) return null;
      const highs = match.map((m) => parseFloat(m.high)).filter((n) => !isNaN(n));
      const lows = match.map((m) => parseFloat(m.low)).filter((n) => !isNaN(n));
      if (!highs.length || !lows.length) return null;
      return `${Math.round(Math.min(...lows))}°C – ${Math.round(Math.max(...highs))}°C`;
    };

    return {
      summer: getRange(['Mar', 'Apr', 'May', 'Jun']) || '20°C – 36°C',
      monsoon: getRange(['Jul', 'Aug', 'Sep']) || '22°C – 32°C',
      autumn: getRange(['Oct', 'Nov']) || '15°C – 28°C',
      winter: getRange(['Dec', 'Jan', 'Feb']) || '10°C – 24°C',
    };
  }, [temple]);

  // Helper: Route icons mapping
  const getTransportIconSvg = (mode = '') => {
    const m = mode.toLowerCase();
    if (m.includes('flight') || m.includes('air')) return '/assets/route-icon/flight.svg';
    if (m.includes('train') || m.includes('rail')) return '/assets/route-icon/train.svg';
    if (m.includes('bus')) return '/assets/route-icon/bus.svg';
    if (m.includes('taxi') || m.includes('cab')) return '/assets/route-icon/texi.svg';
    if (m.includes('auto')) return '/assets/route-icon/auto.svg';
    if (m.includes('battery') || m.includes('car') || m.includes('electric'))
      return '/assets/route-icon/battry-car.svg';
    return '/assets/route-icon/texi.svg';
  };

  // Helper: Accommodation icons mapping
  const getAccommodationIcon = (index) => {
    const icons = ['beds.svg', 'icon1.svg', 'icon3.svg', 'icon4.svg', 'icon5.svg', 'icon6.svg'];
    return `/assets/accommodation-icon/${icons[index % icons.length]}`;
  };

  // Helper: Footfall peak / off-peak
  const footfallData = useMemo(() => {
    const list = temple.footfall?.monthlyFootfalls || [];
    const validMonths = list.filter((m) =>
      ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].includes(
        m.month
      )
    );

    if (validMonths.length >= 4) {
      return {
        peak: validMonths.slice(0, 5),
        offPeak: validMonths.slice(5, 8),
      };
    }

    return {
      peak: [
        { month: 'Oct', totalYatra: '10.5 L' },
        { month: 'Nov', totalYatra: '9.8 L' },
        { month: 'Dec', totalYatra: '12.0 L' },
        { month: 'Mar', totalYatra: '8.5 L' },
        { month: 'Festivals', totalYatra: 'Peak' },
      ],
      offPeak: [
        { month: 'Jul', totalYatra: '5.2 L' },
        { month: 'Aug', totalYatra: '4.8 L' },
        { month: 'Jan', totalYatra: '6.1 L' },
      ],
    };
  }, [temple]);

  // Helper: Registration cards
  const regBoxes = useMemo(() => {
    const list = temple.registrationProcess || [];
    if (list.length > 0) {
      const main = list[0];
      return [
        {
          num: '01',
          text: (
            <>
              {main.description ||
                'Advance registration or entry token is advised for all pilgrims visiting the holy shrine.'}{' '}
              {main.isMandatory && (
                <span>Entry without valid token / biometric registration may be restricted during peak hours.</span>
              )}
            </>
          ),
        },
        {
          num: '02',
          text: (
            <>
              Registration can be done online through the official shrine portal:{' '}
              <Link href={main.website || temple.management?.website || temple.templeAdministration?.website || '#'} target="_blank" rel="noreferrer">
                {main.website || temple.management?.website || temple.templeAdministration?.website || 'Official Temple Portal'}
              </Link>{' '}
              or at designated on-spot counters.
            </>
          ),
        },
        {
          num: '03',
          text: (
            <>
              <span>Pilgrims must carry original valid government photo ID</span> matching their registration slip.
              Reporting at the entrance within the designated time slot is mandatory.
            </>
          ),
        },
      ];
    }

    return [
      {
        num: '01',
        text: (
          <>
            A valid darshan slip or online booking is recommended. Daily pilgrim entry is regulated to manage crowd
            flow.{' '}
            <span>Always carry a government-issued photo ID.</span>
          </>
        ),
      },
      {
        num: '02',
        text: (
          <>
            Yatra registration and special pooja bookings can be completed online through the official temple portal:{' '}
            <Link href={temple.management?.website || temple.templeAdministration?.website || '#'} target="_blank" rel="noreferrer">
              {temple.management?.website || temple.templeAdministration?.website || 'Temple Trust Website'}
            </Link>
          </>
        ),
      },
      {
        num: '03',
        text: (
          <>
            <span>Devotees should report to the assigned queue complex</span> at least 30 minutes before their allotted
            darshan or aarti slot.
          </>
        ),
      },
    ];
  }, [temple]);

  // Aartis and timings
  const mandir = temple.templeTimings?.mandirTimings || {};
  const gufa = temple.templeTimings?.gufaTimings || {};
  const aartis = temple.templeTimings?.aartis || [];
  const primaryAartis = aartis.slice(0, 3);
  const remainingAartis = aartis.length > 3 ? aartis.slice(3) : aartis;

  // Extract Explore Nearby information matching production layout
  const nearbyPlaces = temple.nearbyPlaces || temple.nearbyAttractions || [];
  const isAyodhya = temple.slug?.includes('ayodhya');
  const isVaishno = temple.slug?.includes('vaishno');
  const locCity = temple.generalInfo?.location?.split(',')[0]?.trim() || 'Temple area';

  const medFac = temple.facilities?.find(
    (f) => f.name.toLowerCase().includes('medical') || f.name.toLowerCase().includes('hospital')
  )?.description;
  const foodFac = temple.facilities?.find(
    (f) => f.name.toLowerCase().includes('food') || f.name.toLowerCase().includes('restaurant')
  )?.description;
  const prasadFac = temple.facilities?.find((f) => f.name.toLowerCase().includes('prasad'))?.description;

  let hospitalText = '';
  let pharmacyText = '';
  if (isAyodhya) {
    hospitalText = 'Ayodhya Medical College and Hospital (0.5 km) Civil Hospital (1.5 km)';
    pharmacyText = 'CareZone Pharmacy (0.4 km), Wellness Pharmacy (0.6 km)';
  } else if (isVaishno) {
    hospitalText = 'Katra Hospital (1 km), Bhawan Hospital (3 km)';
    pharmacyText = 'Vaishno Devi Medicals (0.4 km), Cave Pharmacy (0.3 km)';
  } else if (medFac) {
    const parts = medFac.split(/(?<=[.!?])\s+(?=[A-Z])/);
    if (parts.length > 1) {
      hospitalText = parts[0];
      pharmacyText = parts.slice(1).join(' ');
    } else {
      hospitalText = medFac;
      pharmacyText = '24/7 retail pharmacy and medical store located within 500m of temple approach.';
    }
  } else {
    hospitalText = `${locCity} Civil Hospital & Emergency Medical Centre (approx. 1-2 km).`;
    pharmacyText = '24/7 retail pharmacy and medical store located within 500m of temple approach.';
  }

  const foodText = isAyodhya
    ? 'Ram Mandir Dining (0.5 km), Ayodhya Kitchen (1 km)'
    : isVaishno
    ? 'Adkuwari – 1, Sanjichhat – 1, Bhawan – 3'
    : foodFac || 'Pure vegetarian dining halls, Bhojanalayas, and local restaurants available in temple vicinity.';

  const prasadText = isAyodhya
    ? 'Avail prasad inside Ram Janmabhoomi complex and receive it directly from the temple.'
    : prasadFac || 'Avail authentic temple prasad and sacred offerings directly from official temple counters.';

  if (dedicatedRoute) {
    return <Navigate to={dedicatedRoute} replace />;
  }

  return (
    <div className="dynamic-temple-detail-page">
      {/* 1. Inner Banner */}
      <Box className="innerbanner">
        <img
          src={temple.images?.bannerImage || temple.images?.timingImage || '/assets/banner/detail-image.svg'}
          alt={temple.generalInfo.templeName}
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = '/assets/banner/detail-image.svg';
          }}
        />
        <Box className="innerContent">
          <h1>{temple.generalInfo.templeName}</h1>
        </Box>
      </Box>

      {/* 2. Temple Detail Box (About) */}
      <Box className="templeDetailBox">
        <Container maxWidth="md">
          <Grid container spacing={2} justifyContent="center" alignItems="center">
            <Grid size={{ xl: 8, lg: 8, md: 8, sm: 12, xs: 12 }}>
              <Box className="detailBoxContent" sx={{ position: 'relative' }}>
                <p>{temple.generalInfo.templeDescription}</p>
                <div style={{ marginTop: '12px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  <span
                    style={{
                      fontSize: '13px',
                      background: '#e0f7f5',
                      color: '#008b7d',
                      padding: '4px 12px',
                      borderRadius: '15px',
                      fontWeight: 600,
                    }}
                  >
                    📍 {temple.generalInfo.location}
                  </span>
                  <span
                    style={{
                      fontSize: '13px',
                      background: '#fff5e6',
                      color: '#b86214',
                      padding: '4px 12px',
                      borderRadius: '15px',
                      fontWeight: 600,
                    }}
                  >
                    🛕 {temple.generalInfo.deity}
                  </span>
                </div>
              </Box>
            </Grid>
            <Grid size={{ xl: 4, lg: 4, md: 4, sm: 12, xs: 12 }}>
              <Box className="detailBoxImg">
                <img
                  src={temple.images?.thumbnailImage || temple.images?.timingImage || '/assets/vaisno-image.svg'}
                  alt={temple.generalInfo.templeName}
                  className="img-fluid"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = '/assets/vaisno-image.svg';
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* 3. Monthly Temperature Guide */}
      <section>
        <Box className="tempratureBox">
          <Container maxWidth="md">
            <Grid container spacing={2} alignItems="center">
              <Grid size={12}>
                <Box className="templeInnerHeading">
                  <h2>Monthly Temperature Guide</h2>
                </Box>
              </Grid>

              <Grid size={{ xl: 3, lg: 3, md: 3, sm: 6, xs: 6 }}>
                <Box className="tempratureContentBox">
                  <img src="/assets/march-june.svg" alt="March to June" className="w-100" />
                  <h3>
                    March – June
                    <br />
                    <span>{seasonTemps.summer}</span>
                  </h3>
                </Box>
              </Grid>

              <Grid size={{ xl: 3, lg: 3, md: 3, sm: 6, xs: 6 }}>
                <Box className="tempratureContentBox">
                  <img src="/assets/july-sept.svg" alt="July to September" className="w-100" />
                  <h3>
                    July – Sept
                    <br />
                    <span>{seasonTemps.monsoon}</span>
                  </h3>
                </Box>
              </Grid>

              <Grid size={{ xl: 3, lg: 3, md: 3, sm: 6, xs: 6 }}>
                <Box className="tempratureContentBox">
                  <img src="/assets/oct-nov.svg" alt="October to November" className="w-100" />
                  <h3>
                    Oct – Nov
                    <br />
                    <span>{seasonTemps.autumn}</span>
                  </h3>
                </Box>
              </Grid>

              <Grid size={{ xl: 3, lg: 3, md: 3, sm: 6, xs: 6 }}>
                <Box className="tempratureContentBox">
                  <img src="/assets/dec-feb.svg" alt="December to February" className="w-100" />
                  <h3>
                    Dec – Feb
                    <br />
                    <span>{seasonTemps.winter}</span>
                  </h3>
                </Box>
              </Grid>

              {/* Extra Data: Complete 12-Month Pill Breakdown */}
              {temple.temperature?.monthlyTemperatures?.length > 0 && (
                <Grid size={12}>
                  <Box className="twelveMonthBox">
                    <Box className="twelveMonthHeader" onClick={() => setShowAllMonths(!showAllMonths)}>
                      <h4>View Complete 12-Month High / Low Temperatures</h4>
                      <span>{showAllMonths ? '▲ Hide Details' : '▼ Expand All Months'}</span>
                    </Box>

                    {showAllMonths && (
                      <Box className="monthGrid">
                        {temple.temperature.monthlyTemperatures.map((m) => (
                          <Box className="monthPill" key={m.month}>
                            <p className="pMonth">{m.month}</p>
                            <p className="pHigh">High: {m.high}°C</p>
                            <p className="pLow">Low: {m.low}°C</p>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                </Grid>
              )}
            </Grid>
          </Container>
        </Box>
      </section>

      {/* 4. Temple Timings & Aarti */}
      <section>
        <Box className="tampleTimingBox">
          <Container maxWidth="md">
            <Grid container spacing={2} alignItems="stretch">
              <Grid size={{ xl: 6, lg: 6, md: 6, sm: 12, xs: 12 }}>
                <Box className="tampleTimingImg timingMainImg">
                  <img
                    src={temple.images?.timingImage || '/assets/tample-timing.svg'}
                    className="img-fluid"
                    alt={`${temple.generalInfo.templeName} Timings`}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = '/assets/tample-timing.svg';
                    }}
                  />
                </Box>
              </Grid>
              <Grid size={{ xl: 6, lg: 6, md: 6, sm: 12, xs: 12 }}>
                <Box className="tampleTimingContent grayBox p-lg-4">
                  <Box className="templeInnerHeading">
                    <h3>Temple Timings</h3>
                  </Box>
                  <ul>
                    <li>
                      Mandir Opening
                      <span>{mandir.morningOpen || '05:00 AM'}</span>
                    </li>
                    {mandir.morningClose && mandir.morningClose !== 'Open continuously' && (
                      <li>
                        Morning Darshan Closes
                        <span>{mandir.morningClose}</span>
                      </li>
                    )}
                    {mandir.eveningOpen && mandir.eveningOpen !== 'Open continuously' && (
                      <li>
                        Evening Darshan Opens
                        <span>{mandir.eveningOpen}</span>
                      </li>
                    )}
                    <li>
                      Mandir Closing
                      <span>{mandir.eveningClose || '10:00 PM'}</span>
                    </li>

                    {gufa.morningOpen && gufa.morningOpen !== 'N/A' && (
                      <li>
                        Sanctum / Gufa Hours
                        <span>
                          {gufa.morningOpen} to {gufa.eveningClose}
                        </span>
                      </li>
                    )}

                    {primaryAartis.map((a, i) => (
                      <li key={i}>
                        {a.name}
                        <span>{a.time}</span>
                      </li>
                    ))}
                  </ul>
                </Box>
              </Grid>
            </Grid>

            {/* Row 2: Aarti Schedule */}
            <Grid container spacing={2} alignItems="stretch" className="mt-4">
              <Grid size={{ xl: 3, lg: 3, md: 3, sm: 6, xs: 6 }}>
                <Box className="tampleTimingImg timingAartiImg">
                  <img
                    src={temple.images?.aarti1 || '/assets/banner/vr-darshan/temples/aarti.webp'}
                    className="img-fluid"
                    alt={`${temple.generalInfo.templeName} Aarti`}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = '/assets/banner/vr-darshan/temples/aarti.webp';
                    }}
                  />
                </Box>
              </Grid>
              <Grid size={{ xl: 3, lg: 3, md: 3, sm: 6, xs: 6 }}>
                <Box className="tampleTimingImg timingAartiImg">
                  <img
                    src={temple.images?.aarti2 || '/assets/aarti2.svg'}
                    className="img-fluid"
                    alt={`${temple.generalInfo.templeName} Daily Aarti`}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = '/assets/aarti2.svg';
                    }}
                  />
                </Box>
              </Grid>
              <Grid size={{ xl: 6, lg: 6, md: 6, sm: 12, xs: 12 }}>
                <Box className="tampleTimingContent grayBox p-lg-4">
                  <Box className="templeInnerHeading">
                    <h3>Daily Aarti Schedule</h3>
                  </Box>
                  <ul>
                    {remainingAartis.map((art, idx) => (
                      <li key={idx}>
                        {art.name}
                        <span>{art.time}</span>
                      </li>
                    ))}
                  </ul>
                </Box>
              </Grid>
            </Grid>

            {/* 5. Plan Your Route */}
            <Grid container spacing={2} alignItems="center" className="mt-4" justifyContent="center">
              <Grid size={{ xl: 12, lg: 12, md: 12, sm: 12, xs: 12 }}>
                <Box className="templeInnerHeading">
                  <h2>Plan Your Route</h2>
                </Box>
              </Grid>

              <Grid size={{ xl: 12, lg: 12, md: 12, sm: 6, xs: 12 }}>
                <Box className="routeBox">
                  {temple.transportation?.map((tr, index) => (
                    <Box className="routeInnerBox" key={index}>
                      <Box className="route1">
                        <img src={getTransportIconSvg(tr.mode)} alt={tr.mode} className="img-fluid" />
                        <h3>{tr.mode}</h3>
                      </Box>
                      <Box className="route2">
                        <img src="/assets/route-icon/border.svg" alt="" className="img-fluid" />
                      </Box>
                      <Box className="route3">
                        {tr.startPoint && tr.endPoint ? (
                          <p className="mb-1">
                            <span>{tr.startPoint}</span> to {tr.endPoint}
                          </p>
                        ) : tr.startPoint ? (
                          <p className="mb-1">
                            <span>{tr.startPoint}</span>
                          </p>
                        ) : null}
                        {(tr.distanceKm || tr.duration || tr.cost) && (
                          <p>
                            {tr.distanceKm ? `${tr.distanceKm} km, ` : ''}
                            {tr.duration ? `${tr.duration}${tr.cost ? ', ' : ''}` : ''}
                            {tr.cost ? `${tr.cost}` : ''}
                          </p>
                        )}
                        {tr.extraInfo && <p>{tr.extraInfo}</p>}
                        {tr.description && <p>{tr.description}</p>}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Box>
      </section>

      {/* 6. Yatra Parchi / Registration */}
      <Box className="lightGrayBox">
        <Container maxWidth="md">
          <Grid container spacing={3} justifyContent="center" alignItems="stretch">
            <Grid size={{ xl: 12, lg: 12, md: 12, sm: 12, xs: 12 }}>
              <Box className="templeInnerHeading">
                <h2>Yatra Parchi / Registration</h2>
                <p>Registration Guidelines, Official Portals & Verification</p>
              </Box>
            </Grid>

            {regBoxes.map((box, idx) => (
              <Grid
                size={{ xl: 4, lg: 4, md: 4, sm: 6, xs: 12 }}
                key={idx}
                sx={{ display: 'flex' }}
              >
                <Box className={`registrationzBox ${idx === regBoxes.length - 1 ? 'lastBox' : ''}`}>
                  <h2>{box.num}</h2>
                  <p>{box.text}</p>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* 7. Blessings That Attract Millions (Footfall) */}
      <section>
        <Container maxWidth="md">
          <Grid container spacing={2} justifyContent="center" alignItems="center">
            <Grid size={{ xl: 12, lg: 12, md: 12, sm: 12, xs: 12 }}>
              <Box className="templeInnerHeading">
                <h2>Blessings That Attract Millions</h2>
              </Box>
            </Grid>

            <Grid size={{ xl: 12, lg: 12, md: 12, sm: 12, xs: 12 }}>
              <Box className="templeInnerHeading mb-4">
                <h3>Peak Months</h3>
                <p>(High Footfall Period - {temple.timeInfo?.peakTime || 'Festivals & Holidays'})</p>
              </Box>
            </Grid>

            {footfallData.peak.map((item, idx) => (
              <Grid size={{ xl: 2.4, lg: 2.4, md: 2.4, sm: 6, xs: 6 }} key={idx}>
                <Box className="peakMonthBox">
                  <Box>
                    <p>{item.month}</p>
                    <span>{item.totalYatra}</span>
                  </Box>
                </Box>
              </Grid>
            ))}

            <Grid size={{ xl: 12, lg: 12, md: 12, sm: 12, xs: 12 }}>
              <Box className="templeInnerHeading mt-4">
                <h3>Off - Peak Months</h3>
                <p>(Normal Pilgrimage Flow)</p>
              </Box>
            </Grid>

            {footfallData.offPeak.map((item, idx) => (
              <Grid size={{ xl: 2.4, lg: 2.4, md: 2.4, sm: 6, xs: 6 }} key={idx}>
                <Box className="offPeakMonthBox">
                  <Box>
                    <p>{item.month}</p>
                    <span>{item.totalYatra}</span>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </section>

      {/* 8. Accommodation Information */}
      <Box className="lightGrayBox">
        <Container maxWidth="md">
          <Grid container spacing={2} justifyContent="center" alignItems="center">
            <Grid size={{ xl: 12, lg: 12, md: 12, sm: 12, xs: 12 }}>
              <Box className="templeInnerHeading mb-lg-4">
                <h2>Accommodation Information</h2>
              </Box>
            </Grid>

            {temple.accommodation?.map((acc, index) => (
              <Grid size={{ xl: 6, lg: 6, md: 6, sm: 6, xs: 12 }} key={index}>
                <Box className="accommodationBox">
                  <Box className="accommodationImg">
                    <img src={getAccommodationIcon(index)} className="img-fluid" alt="" />
                  </Box>
                  <Box className="accommodationContent">
                    <h3>
                      {acc.name} {acc.type ? `(${acc.type})` : ''}
                    </h3>
                    <ul>
                      <li>
                        <span>Location:</span> {acc.location}{' '}
                        {acc.distanceKm ? `(${acc.distanceKm} km from temple)` : ''}
                      </li>
                      <li>
                        <span>Tariff:</span> {acc.cost || 'Affordable trust rates'}
                      </li>
                      <li>
                        <span>Info:</span> {acc.bookingInfo}
                      </li>
                      {acc.contact && (
                        <li>
                          <span>Contact:</span> {acc.contact}
                        </li>
                      )}
                    </ul>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* 9. Here’s What You Should Know */}
      <section className="lightGrayBox">
        <Container maxWidth="md">
          <Grid container spacing={2} justifyContent="center" alignItems="stretch">
            <Grid size={{ xl: 12, lg: 12, md: 12, sm: 12, xs: 12 }}>
              <Box className="templeInnerHeading mb-lg-4">
                <h2>Here’s What You Should Know</h2>
              </Box>
            </Grid>

            {/* 01: Clothing Guidelines */}
            <Grid size={{ xl: 12, lg: 12, md: 12, sm: 12, xs: 12 }}>
              <div className="infoGuideBox">
                <Grid container spacing={2} justifyContent="center" alignItems="center">
                  <Grid size={{ xl: 3, lg: 3, md: 3, sm: 12, xs: 12 }}>
                    <div className="infoHeading">
                      <h2>01</h2>
                      <h3>Clothing Guidelines</h3>
                    </div>
                  </Grid>
                  <Grid size={{ xl: 9, lg: 9, md: 9, sm: 12, xs: 12 }}>
                    <div className="infoList">
                      <ul>
                        <li>
                          {temple.generalInfo?.dressCode ||
                            'Modest, traditional attire is advised. Avoid shorts and western revealing clothes.'}
                        </li>
                        <li>Men generally wear dhoti/kurta; women wear sarees or salwar kameez.</li>
                        <li>Carry light woollens during winter months and rain gear during monsoon season.</li>
                      </ul>
                    </div>
                  </Grid>
                </Grid>
              </div>
            </Grid>

            {/* 02: Entry/Exit Information & Gates */}
            <Grid size={{ xl: 12, lg: 12, md: 12, sm: 12, xs: 12 }}>
              <div className="infoGuideBox">
                <Grid container spacing={2} justifyContent="center" alignItems="center">
                  <Grid size={{ xl: 3, lg: 3, md: 3, sm: 12, xs: 12 }}>
                    <div className="infoHeading">
                      <h2>02</h2>
                      <h3>Entry/Exit Information</h3>
                    </div>
                  </Grid>
                  <Grid size={{ xl: 9, lg: 9, md: 9, sm: 12, xs: 12 }}>
                    <div className="infoList">
                      <h4>Temple Gates & Access:</h4>
                      {temple.entryExitGates?.length > 0 ? (
                        <ul>
                          {temple.entryExitGates.map((gate, i) => (
                            <li key={i}>
                              <span>{gate.gateName}:</span> {gate.location} — {gate.allowedVisitors}.{' '}
                              {gate.restrictions && `(${gate.restrictions})`}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <ul>
                          <li>
                            <span>Main Gate:</span> Open for general public darshan through designated barricaded queues.
                          </li>
                          <li>
                            <span>VIP / Special Gate:</span> For pass holders, senior citizens, and physically challenged
                            devotees.
                          </li>
                        </ul>
                      )}
                      <h4 className="mt-2">Exit Rules:</h4>
                      <p>Pilgrims must follow the designated one-way exit corridors to prevent crowd congestion.</p>
                    </div>
                  </Grid>
                </Grid>
              </div>
            </Grid>

            {/* 03: Photography / Videography */}
            <Grid size={{ xl: 6, lg: 6, md: 6, sm: 12, xs: 12 }}>
              <div className="infoGuideBox">
                <Grid container spacing={2} justifyContent="center" alignItems="center">
                  <Grid size={{ xl: 7, lg: 7, md: 7, sm: 12, xs: 12 }}>
                    <div className="infoHeading">
                      <h2>03</h2>
                      <h3>Photography / Videography</h3>
                    </div>
                  </Grid>
                  <Grid size={{ xl: 5, lg: 5, md: 5, sm: 12, xs: 12 }}>
                    <div className="infoList">
                      <p>
                        Strictly prohibited inside the sanctum sanctorum. Mobile phones and cameras must be deposited in
                        lockers before entry.
                      </p>
                    </div>
                  </Grid>
                </Grid>
              </div>
            </Grid>

            {/* 04: Drinking Water & Restrooms */}
            <Grid size={{ xl: 6, lg: 6, md: 6, sm: 12, xs: 12 }}>
              <div className="infoGuideBox">
                <Grid container spacing={2} justifyContent="center" alignItems="center">
                  <Grid size={{ xl: 7, lg: 7, md: 7, sm: 12, xs: 12 }}>
                    <div className="infoHeading">
                      <h2>04</h2>
                      <h3>Drinking Water & Restrooms</h3>
                    </div>
                  </Grid>
                  <Grid size={{ xl: 5, lg: 5, md: 5, sm: 12, xs: 12 }}>
                    <div className="infoList">
                      <p>
                        Purified RO drinking water kiosks and clean toilet facilities are provided free across temple
                        premises and queue complexes.
                      </p>
                    </div>
                  </Grid>
                </Grid>
              </div>
            </Grid>

            {/* 05: VIP Darshan */}
            <Grid size={{ xl: 12, lg: 12, md: 12, sm: 12, xs: 12 }}>
              <div className="infoGuideBox">
                <Grid container spacing={2} justifyContent="center" alignItems="center">
                  <Grid size={{ xl: 7, lg: 7, md: 7, sm: 12, xs: 12 }}>
                    <Grid container spacing={2} justifyContent="center" alignItems="center">
                      <Grid size={{ xl: 5, lg: 5, md: 5, sm: 12, xs: 12 }}>
                        <div className="infoHeading">
                          <h2>05</h2>
                          <h3>VIP Darshan</h3>
                        </div>
                      </Grid>
                      <Grid size={{ xl: 7, lg: 7, md: 7, sm: 12, xs: 12 }}>
                        <div className="infoList">
                          <h4>Special Queue & Seva:</h4>
                          <ul>
                            <li>Priority lines available for senior citizens, infants & defense personnel.</li>
                            <li>
                              Pass counters are located near the main trust administrative office or online portal.
                            </li>
                          </ul>
                        </div>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid size={{ xl: 5, lg: 5, md: 5, sm: 12, xs: 12 }}>
                    <div className="infoList">
                      <h4>Special Services Available:</h4>
                      {temple.specialServices?.length > 0 ? (
                        <ul>
                          {temple.specialServices.slice(0, 3).map((s, i) => (
                            <li key={i}>
                              {s.name}: {s.cost || 'Available onsite/online'}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p>Special poojas and abhishekams can be booked in advance via the shrine portal.</p>
                      )}
                    </div>
                  </Grid>
                </Grid>
              </div>
            </Grid>

            {/* 06: Smart Locker / Cloak Room */}
            <Grid size={{ xl: 12, lg: 12, md: 12, sm: 12, xs: 12 }}>
              <div className="infoGuideBox">
                <Grid container spacing={2} justifyContent="center" alignItems="center">
                  <Grid size={{ xl: 3, lg: 3, md: 3, sm: 12, xs: 12 }}>
                    <div className="infoHeading">
                      <h2>06</h2>
                      <h3>Smart Locker / Cloak Room</h3>
                    </div>
                  </Grid>
                  <Grid size={{ xl: 9, lg: 9, md: 9, sm: 12, xs: 12 }}>
                    <p>
                      Safe and convenient luggage and electronic lockers are provided near major temple entry gates and
                      facilitation centers.
                    </p>
                    <div className="infoList">
                      <ul>
                        <li>Electronic lockers available for mobile phones, smartwatches, and leather goods.</li>
                        <li>Cloakrooms accommodate suitcases and backpacks at nominal trust rates or free.</li>
                        <li>Keep the locker token/slip safely until collection.</li>
                      </ul>
                    </div>
                  </Grid>
                </Grid>
              </div>
            </Grid>

            {/* 07: Shoe Storage */}
            <Grid size={{ xl: 12, lg: 12, md: 12, sm: 12, xs: 12 }}>
              <div className="infoGuideBox">
                <Grid container spacing={2} justifyContent="center" alignItems="center">
                  <Grid size={{ xl: 3, lg: 3, md: 3, sm: 12, xs: 12 }}>
                    <div className="infoHeading">
                      <h2>07</h2>
                      <h3>Shoe Storage</h3>
                    </div>
                  </Grid>
                  <Grid size={{ xl: 9, lg: 9, md: 9, sm: 12, xs: 12 }}>
                    <div className="infoList">
                      <ul>
                        <li>Free shoe custody counters operate 24/7 near all main entrance gates.</li>
                        <li>Devotees receive a physical token upon submitting footwear.</li>
                      </ul>
                    </div>
                  </Grid>
                </Grid>
              </div>
            </Grid>

            {/* 08: Parking */}
            <Grid size={{ xl: 12, lg: 12, md: 12, sm: 12, xs: 12 }}>
              <div className="infoGuideBox">
                <Grid container spacing={2} justifyContent="center" alignItems="center">
                  <Grid size={{ xl: 3, lg: 3, md: 3, sm: 12, xs: 12 }}>
                    <div className="infoHeading">
                      <h2>08</h2>
                      <h3>Parking</h3>
                    </div>
                  </Grid>
                  <Grid size={{ xl: 9, lg: 9, md: 9, sm: 12, xs: 12 }}>
                    <div className="infoList">
                      <ul>
                        <li>
                          Designated multi-level / open parking lots are located within 500m to 1.5 km of the temple.
                        </li>
                        <li>Nominal charges: approx ₹20–₹50 for two-wheelers, ₹50–₹100 for cars/SUVs.</li>
                      </ul>
                    </div>
                  </Grid>
                </Grid>
              </div>
            </Grid>

            {/* 09: Bhojanalayas & Annadanam */}
            <Grid size={{ xl: 12, lg: 12, md: 12, sm: 12, xs: 12 }}>
              <div className="infoGuideBox">
                <Grid container spacing={2} justifyContent="center" alignItems="center">
                  <Grid size={{ xl: 3, lg: 3, md: 3, sm: 12, xs: 12 }}>
                    <div className="infoHeading">
                      <h2>09</h2>
                      <h3>Bhojanalayas & Annadanam</h3>
                    </div>
                  </Grid>
                  <Grid size={{ xl: 9, lg: 9, md: 9, sm: 12, xs: 12 }}>
                    <p>
                      The temple trust operates free Annadanam halls or affordable canteens serving fresh, pure
                      vegetarian satvik meals and sacred Mahaprasad to all devotees.
                    </p>
                    <div className="infoList">
                      <ul>
                        <li>
                          <span>Timings:</span> Lunch approx 11:30 AM – 3:00 PM; Dinner approx 7:00 PM – 9:30 PM.
                        </li>
                        <li>
                          <span>Prasad Counters:</span> Traditional sweets and laddoos available at fixed trust rates.
                        </li>
                      </ul>
                    </div>
                  </Grid>
                </Grid>
              </div>
            </Grid>

            {/* 10: Special Amenities */}
            <Grid size={{ xl: 12, lg: 12, md: 12, sm: 12, xs: 12 }}>
              <div className="infoGuideBox">
                <Grid container spacing={2} justifyContent="center" alignItems="center">
                  <Grid size={{ xl: 3, lg: 3, md: 3, sm: 12, xs: 12 }}>
                    <div className="infoHeading">
                      <h2>10</h2>
                      <h3>Special Amenities</h3>
                    </div>
                  </Grid>
                  <Grid size={{ xl: 9, lg: 9, md: 9, sm: 12, xs: 12 }}>
                    <div className="infoList">
                      <ul>
                        <li>
                          <span>Wheelchair Service:</span> Available free of charge for senior citizens and disabled
                          pilgrims at entry gates.
                        </li>
                        <li>
                          <span>Battery Operated Vehicles:</span> Available along designated pathways for elderly
                          devotees.
                        </li>
                        <li>
                          <span>First Aid & Medical:</span> 24/7 basic medical post with ambulance on standby.
                        </li>
                      </ul>
                    </div>
                  </Grid>
                </Grid>
              </div>
            </Grid>
          </Grid>
        </Container>
      </section>

      {/* 10. Explore Nearby */}
      <section>
        <Container maxWidth="md">
          <Grid container spacing={2} justifyContent="center" alignItems="center">
            <Grid size={{ xl: 12, lg: 12, md: 12, sm: 12, xs: 12 }}>
              <Box className="templeInnerHeading mb-lg-4">
                <h2>Explore Nearby</h2>
              </Box>
            </Grid>

            <Grid size={{ xl: 12, lg: 12, md: 12, sm: 12, xs: 12 }}>
              <Box className="explore-nearby">
                <Grid container spacing={2}>
                  {/* Left Column */}
                  <Grid size={{ xl: 7, lg: 7, md: 7, sm: 12, xs: 12 }}>
                    <Box className="info-box">
                      <Typography className="title">Tourist Attractions</Typography>
                      {nearbyPlaces.length > 0 ? (
                        nearbyPlaces.map((att, idx) => (
                          <div className="block" key={idx}>
                            <span>{typeof att === 'string' ? att : att.name}</span>
                            {att.description && <p>{att.description}</p>}
                            {att.distanceKm && !att.description && (
                              <p>
                                {att.distanceKm} km {att.attractionType ? `(${att.attractionType})` : ''}
                              </p>
                            )}
                          </div>
                        ))
                      ) : (
                        <div className="block">
                          <span>Local Sacred Shrines & Ghats</span>
                          <p>Multiple ancient shrines and cultural heritage sites located within 5 km.</p>
                        </div>
                      )}
                    </Box>

                    <Box className="info-box mt-3">
                      <Typography className="title">ATMs & Banks</Typography>
                      {isVaishno ? (
                        <>
                          <p>
                            <span>Katra</span> – OBC, UCO Bank, J&K Bank, Ellaqui Dehati Bank
                          </p>
                          <p>
                            <span>Adhkuwari</span> – J&K Bank
                          </p>
                          <p>
                            <span>Bhawan</span> – OBC, J&K Bank
                          </p>
                        </>
                      ) : isAyodhya ? (
                        <>
                          <p>State Bank of India (0.2 km)</p>
                          <p>Axis Bank (0.5 km)</p>
                        </>
                      ) : (
                        <>
                          <p>State Bank of India (0.2 km)</p>
                          <p>HDFC Bank ATM (0.4 km)</p>
                          <p>Axis Bank (0.5 km)</p>
                        </>
                      )}
                    </Box>

                    {isAyodhya && (
                      <Box className="info-box mt-3">
                        <Typography className="title">Boating Available in Ayodhya Saryu River</Typography>
                        <p>
                          <span>1.</span> The Jatayu Cruise is a Ramayana-themed 18 km journey from Naya Ghat to Guptar Ghat, priced at ₹300 per person.
                        </p>
                        <p>
                          <span>2.</span> At Saryu Ghat, boat rides start at ₹150 for long trips and ₹60 for shorter ones, with group and solo options offering scenic, peaceful river views.
                        </p>
                      </Box>
                    )}

                    {isVaishno && (
                      <Box className="info-box mt-3">
                        <Typography className="title">VIP Darshan</Typography>
                        <p>Free for Defence (pass at Army counter near Shiv Temple).</p>
                        <p>
                          Free for Helicopter Yatris, and for those availing Attka Aarti, Garbhjoon Aarti, SSVP, Nav Chandi Path, and Havan Pooja services.
                        </p>
                        <p>
                          <span>Price</span> – ₹ 1830 (One-way Katra-sanjichaat) / ₹3660 round trip
                        </p>
                      </Box>
                    )}

                    {!isAyodhya && !isVaishno && temple.management && (temple.management.trustName || temple.management.contactNumber) && (
                      <Box className="info-box mt-3">
                        <Typography className="title">Temple Trust & Helpline</Typography>
                        {temple.management.trustName && (
                          <p>
                            <span>Trust:</span> {temple.management.trustName}
                          </p>
                        )}
                        {temple.management.contactNumber && (
                          <p>
                            <span>Helpline:</span> {temple.management.contactNumber}
                          </p>
                        )}
                        {temple.management.website && (
                          <p>
                            <span>Website:</span>{' '}
                            <Link href={temple.management.website} target="_blank" rel="noreferrer">
                              {temple.management.website}
                            </Link>
                          </p>
                        )}
                      </Box>
                    )}
                  </Grid>

                  {/* Right Column */}
                  <Grid size={{ xl: 5, lg: 5, md: 5, sm: 12, xs: 12 }}>
                    <Grid container spacing={2}>
                      <Grid size={{ xl: 12, lg: 12, md: 12, sm: 12, xs: 12 }}>
                        <Box className="info-box">
                          <Typography className="title">Hospitals</Typography>
                          <p>{hospitalText}</p>
                        </Box>
                      </Grid>

                      <Grid size={{ xl: 12, lg: 12, md: 12, sm: 12, xs: 12 }}>
                        <Box className="info-box">
                          <Typography className="title">Pharmacies</Typography>
                          <p>{pharmacyText}</p>
                        </Box>
                      </Grid>

                      <Grid size={{ xl: 12, lg: 12, md: 12, sm: 12, xs: 12 }}>
                        <Box className="info-box">
                          <Typography className="title">Food Options</Typography>
                          <p>{foodText}</p>
                        </Box>
                      </Grid>

                      <Grid size={{ xl: 12, lg: 12, md: 12, sm: 12, xs: 12 }}>
                        <Box className="info-box">
                          <Typography className="title">Prasad Counter</Typography>
                          <p>{prasadText}</p>
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </section>

      {/* 11. Extra Data: Temple Track & Milestones (For Trek Temples - Kedarnath, Sabarimala, Pavagadh, Mansa Devi etc.) */}
      {temple.generalInfo?.trekPoints && temple.generalInfo.trekPoints.length > 0 && (
        <section className="p-0 mt-5 pb-5">
          <Container maxWidth="md">
            <Grid container spacing={2} justifyContent="center" alignItems="center">
              <Grid size={{ xl: 12, lg: 12, md: 12, sm: 12, xs: 12 }}>
                <Box className="templeInnerHeading mb-lg-1">
                  <h2>Temple Track & Milestones</h2>
                  <p>Checkpoints, trek distance, elevation and transit services along the sacred route</p>
                </Box>
              </Grid>

              <Grid size={{ xl: 12, lg: 12, md: 12, sm: 12, xs: 12 }}>
                <Box className="trekMilestonesBox">
                  {temple.generalInfo.trekPoints.map((tp, idx) => (
                    <Box className="milestoneItem" key={idx}>
                      <Box className="msInfo">
                        <h4>
                          {idx + 1}. {tp.pointName || tp.name || `Checkpoint ${idx + 1}`}
                        </h4>
                        <p>{tp.description || tp.details || 'Pilgrim transit station & rest stop'}</p>
                      </Box>
                      <Box className="msTags">
                        {tp.distanceKm && <span className="msTag">Distance: {tp.distanceKm} km</span>}
                        {tp.altitude && <span className="msTagAlt">Alt: {tp.altitude}</span>}
                        {tp.facilities && <span className="msTag">{tp.facilities}</span>}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Container>
        </section>
      )}
    </div>
  );
}
